import type { Collection, CollectionId } from '@/scopes/search/search/infrastructure/api/types'
import { useIntl } from '@/intl'

import { LazyImage } from '@/scopes/common/components/Image'

import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'

import { mqMin, radius, space, tok, tokPx } from '@sunwebgroup/ui-components'
import { Icon, IconGlyph, Typography } from '@sunwebgroup/ui-components/uds'

import { css, type RuleSet, styled } from 'styled-components'
import * as List from './ListItem'

interface CollectionBannerProps {
  collection: Collection
  description: string
  image: string
  title: string
  usps: string[]
}

const LIST_ITEM_ICON_SIZE = 24

const Container = styled.div<{ $id: CollectionId }>`
  overflow: hidden;
  border-radius: ${radius('primitives_corner_radius_l')};
  box-shadow: ${tok('primitives_box_shadows_level_2')};

  @media ${mqMin('primitives_breakpoint_m_min')} {
    display: flex;
    align-items: stretch;
  }

  ${({ $id }) => modCollectionColor($id)}
`

const Badge = styled.div`
  position: absolute;
  z-index: 1;
  margin: ${space('primitives_space_12')};
  border-radius: ${radius('primitives_corner_radius_l')};
  min-width: 72px;
  height: 72px;
  padding: ${tokPx('primitives_space_12')};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${space('primitives_space_2')};
  text-align: center;
`

const LazyImageWrapper = styled.div`
  width: 100%;
  height: 192px;

  @media ${mqMin('primitives_breakpoint_m_min')} {
    display: flex;
    height: auto;
    flex: 1 0 0;
  }

  background: ${tok('semantics_views_tinted_background_color')};
`

const Content = styled.div`
  @media ${mqMin('primitives_breakpoint_m_min')} {
    flex: 2 0 0;
  }

  display: flex;
  flex-direction: column;
  padding: ${space('primitives_space_24')};
`

const Title = styled('Typography')`
  ${Typography.modVariant('display-g-regular')};
  padding-bottom: ${space('primitives_space_8')};
  margin: 0px;
`
const Description = styled('Typography')`
  ${Typography.modVariant('body-m-regular-tight')};
  padding-bottom: ${space('primitives_space_24')};
`

function modCollectionColor(id: CollectionId): RuleSet<object> {
  const backgroundColors = {
    atlantis: tok('primitives_color_collection_atlantis'),
    boutique: tok('primitives_color_collection_boutique'),
    cycling: tok('primitives_color_collection_cycling'),
    excellent: tok('primitives_color_collection_excellent'),
    famfun: tok('primitives_color_collection_famfun'),
    primavera: tok('primitives_color_collection_primavera'),
    secrets: tok('primitives_color_collection_secrets'),
    selections: tok('primitives_color_collection_selections'),
    totally_snow: tok('primitives_color_collection_totally'),
    villages: tok('primitives_color_collection_villages'),
    xperience: tok('primitives_color_collection_xperience'),
    child: tok('primitives_color_collection_child'),
    pearls: tok('primitives_color_collection_pearls'),
    culinary: tok('primitives_color_collection_culinary'),
    romantic: tok('primitives_color_collection_honeymoon'),
    newest_discoveries: tok('primitives_color_collection_newest_discoveries'),
  }

  return css`
    ${Badge},
    ${Content} {
      background-color: ${id in backgroundColors ? backgroundColors[id] : tok('semantics_content_white_color')};
      color: ${id in backgroundColors ? tok('semantics_content_white_color') : tok('primitives_color_primary_500')};
    }
  `
}

function getCollectionName(intl: any, collectionId: CollectionId) {
  const translationIds = {
    atlantis: 'collection_name_atlantis',
    boutique: 'collection_name_boutique',
    cycling: 'collection_name_cycling',
    excellent: 'collection_name_excellent',
    famfun: 'collection_name_famfun',
    primavera: 'collection_name_primavera',
    secrets: 'collection_name_secrets',
    selections: 'collection_name_selections',
    totally_snow: 'collection_name_totally_snow',
    villages: 'collection_name_villages',
    xperience: 'collection_name_xperience',
    child: 'collection_name_child',
    pearls: 'collection_name_pearls',
    culinary: 'collection_name_culinary',
    romantic: 'collection_name_romantic',
    newest_discoveries: 'collection_name_newest_discoveries',
  }
  const translationId = collectionId in translationIds ? translationIds[collectionId] : 'collection_name_unknown'

  return intl.formatMessage({ id: translationId })
}

function getCollectionIcon(collectionId: string, brandName: string): IconGlyph {
  if (collectionId === 'primavera') {
    return 'LogoPrimavera'
  }

  if (collectionId === 'totally_snow') {
    return 'LogoTotally'
  }

  if (brandName === 'eliza') {
    return 'LogoElizaWasHere'
  }

  return 'LogoSunweb'
}

export function CollectionBanner({ collection, description, image, title, usps }: CollectionBannerProps) {
  const intl = useIntl()
  const brandName = useConfiguration('brandName')

  return (
    <Container $id={collection.id}>
      <Badge>
        <Icon glyph={getCollectionIcon(collection.id, brandName)} size={32} />
        <Typography $variant="body-xs-bold-flat" as="span">
          {collection.text || getCollectionName(intl, collection.id)}
        </Typography>
      </Badge>
      <LazyImageWrapper>
        <LazyImage src={image} alt={title} />
      </LazyImageWrapper>
      <Content>
        <Title as="h4">
          {title}
        </Title>
        <Description>
          {description}
        </Description>
        <List.Root>
          {usps.map((usp, index) => (
            <List.Item key={index} $size={LIST_ITEM_ICON_SIZE}>
              <Typography $variant="body-m-regular-tight">{usp}</Typography>
            </List.Item>
          ))}
        </List.Root>
      </Content>
    </Container>
  )
}

CollectionBanner.displayName = 'CollectionBanner'
